import { WittySuggestFieldRequest } from '@agilelab/plugin-wb-witty-common';
import {
  AiGenerateActionButton,
  handleWittyError,
  isWittyMultiSelectFieldSubscription,
  useWittyFormContext,
  useWittyTable,
  wittyAutocompleteApiRef,
  WittyNoContextTooltip,
  WittyTableContextProvider,
} from '@agilelab/plugin-wb-witty-react';
import { alertApiRef, configApiRef, useApi } from '@mainstage/core-plugin-api';
import { Divider, useTheme } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import AddIcon from '@material-ui/icons/AddCircle';
import { JSONSchema7 } from 'json-schema';
import { useCallback, useMemo, useState } from 'react';
import {
  WbWidget,
  WbButton,
  useResponsiveMobile,
} from '@agilelab/plugin-wb-platform';
import { scaffolderPlugin } from '../../../plugin';
import { createScaffolderLayout } from '../../../layouts';
import { ArrayTableContent } from '../ArrayFieldTemplate/ArrayTableTemplate';
import { getValue, upsertValues } from './fieldWriter';
import {
  WittyTableTemplateConfiguration,
  WittyTableTemplateConfigurationZod,
} from './types';
import { IChangeEvent } from '@rjsf/core';
import { ArrayFieldTemplateProps } from '@rjsf/utils';

// TODO: re-implement chunks > 1
const DEFAULT_MAX_ROWS_PER_CHUNK = 1;

export type WittyTableTemplateProps = ArrayFieldTemplateProps & {
  schema: JSONSchema7 & { witty: WittyTableTemplateConfiguration };
};

// returns a set of optionsSetIds for multi-select subscriptions
const getSubscribedOptionsSetIds = (wittyTable: any): Set<string> =>
  new Set(
    wittyTable.wittyFields
      .filter(isWittyMultiSelectFieldSubscription)
      .map((s: any) => s.optionsSetId),
  );

// converts table fields into an array of suggestion field objects
const buildSuggestFields = (wittyTable: any): WittySuggestFieldRequest[] =>
  wittyTable.wittyFields.map((sub: any) =>
    sub.type === 'text'
      ? { path: sub.fieldId, type: 'text' }
      : {
          path: sub.fieldId,
          type: 'multiselect',
          optionsSet: sub.optionsSetId,
        },
  );

// groups suggestion fields by “row”, using the substring of the fieldId up to the last underscore
const groupSuggestFieldsByRow = (
  wittyTable: any,
  suggestFields: WittySuggestFieldRequest[],
) => {
  const rowsMap: Record<string, WittySuggestFieldRequest[]> = {};
  const rowOrder: string[] = [];

  wittyTable.wittyFields.forEach((sub: any) => {
    const lastUnderscore = sub.fieldId.lastIndexOf('_');
    const rowKey = sub.fieldId.substring(0, lastUnderscore);
    if (!rowsMap[rowKey]) {
      rowsMap[rowKey] = [];
      rowOrder.push(rowKey);
    }
  });

  suggestFields.forEach(field => {
    const lastUnderscore = field.path.lastIndexOf('_');
    const rowKey = field.path.substring(0, lastUnderscore);
    if (!rowsMap[rowKey]) {
      rowsMap[rowKey] = [];
      rowOrder.push(rowKey);
    }
    rowsMap[rowKey].push(field);
  });

  return { rowsMap, rowOrder };
};

// splits the rows (as defined by rowOrder and rowsMap) into chunks,
// with each chunk containing at most `maxRowsPerChunk` rows
const createRowChunks = (
  rowsMap: Record<string, WittySuggestFieldRequest[]>,
  rowOrder: string[],
  maxRowsPerChunk: number,
): WittySuggestFieldRequest[][] => {
  const rowChunks: WittySuggestFieldRequest[][] = [];
  for (let i = 0; i < rowOrder.length; i += maxRowsPerChunk) {
    const rowsInChunk = rowOrder.slice(i, i + maxRowsPerChunk);
    const chunkFields = rowsInChunk.reduce(
      (acc: WittySuggestFieldRequest[], rowKey: string) =>
        acc.concat(rowsMap[rowKey]),
      [],
    );
    rowChunks.push(chunkFields);
  }
  return rowChunks;
};

export const WittyTableTemplate = (props: WittyTableTemplateProps) => {
  const wittyTable = useWittyTable();
  const wittyApi = useApi(wittyAutocompleteApiRef);
  const wittyForm = useWittyFormContext();
  const alertApi = useApi(alertApiRef);
  const configApi = useApi(configApiRef);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  const [overrideWarningOpen, setOverrideWarningOpen] = useState(false);
  const isMobile = useResponsiveMobile();

  // tells whether the user inserted some values into witty-enabled fields that are subscribed to this table
  const shouldOverride = useMemo(() => {
    return wittyTable?.wittyFields.some(sub => {
      const value = getValue(wittyForm?.formData ?? {}, sub.fieldId);
      return (
        value !== null &&
        value !== '' &&
        value?.length !== 0 &&
        Object.keys(value).length > 0
      );
    });
  }, [wittyTable?.wittyFields, wittyForm?.formData]);

  const wittyEnabledFieldsCount = useMemo(
    () => wittyTable?.wittyFields.length ?? 0,
    [wittyTable?.wittyFields],
  );

  const wittyEnabled = useMemo(
    () =>
      configApi.getOptionalBoolean(
        'witty.scaffolder.smartSuggestions.enabled',
      ) ?? false,
    [configApi],
  );

  const buttonDisabled = useMemo(
    () => loading || wittyEnabledFieldsCount === 0,
    [loading, wittyEnabledFieldsCount],
  );

  const transformValues = useCallback(
    (accumulatedSuggestions: { [key: string]: any }) => {
      wittyTable?.wittyFields.forEach(sub => {
        if (sub.type === 'text') return;
        // transform multi-select values
        if (accumulatedSuggestions[sub.fieldId] !== undefined) {
          accumulatedSuggestions[sub.fieldId] = sub.transformBeforeFill(
            accumulatedSuggestions[sub.fieldId],
          );
        }
      });
    },
    [wittyTable],
  );

  const processChunk = useCallback(
    (
      chunk: WittySuggestFieldRequest[],
      optionsSets: any,
      accumulatedSuggestions: { [key: string]: any },
      rowIndex: number,
    ) => {
      if (!wittyTable) throw new Error('Not in a table context');

      return wittyApi
        .autocomplete({
          tablePath: wittyTable?.tablePath,
          formData: props.formContext,
          suggestFields: chunk,
          optionsSets,
          selectRowIndex: rowIndex,
          additionalContext: wittyTable?.additionalContext,
        })
        .then((response: any) => {
          Object.assign(accumulatedSuggestions, response.suggestions);
        });
    },
    [wittyApi, wittyTable, props.formContext],
  );

  // memoize derived values so that they are recomputed only when dependencies change.
  const subscribedOptionsSetIds = useMemo(
    () => getSubscribedOptionsSetIds(wittyTable),
    [wittyTable],
  );
  const allSuggestFields = useMemo(
    () => buildSuggestFields(wittyTable),
    [wittyTable],
  );
  const { rowsMap, rowOrder } = useMemo(
    () => groupSuggestFieldsByRow(wittyTable, allSuggestFields),
    [wittyTable, allSuggestFields],
  );

  const rowChunks = useMemo(
    () => createRowChunks(rowsMap, rowOrder, DEFAULT_MAX_ROWS_PER_CHUNK),
    [rowsMap, rowOrder],
  );

  const suggest = useCallback(async () => {
    setLoading(true);
    try {
      if (!wittyTable) throw new Error('Not in a table context');

      // retrieve the necessary options sets.
      const optionsSets = await wittyTable.getOptionsSets(
        Array.from(subscribedOptionsSetIds),
      );

      let updatedFormData = { ...wittyForm?.formData };
      for (const [rowIndex, rowChunk] of rowChunks.entries()) {
        // accumulate suggestions as each chunk is processed.
        const accumulatedSuggestions: { [key: string]: any } = {};
        const promises = rowChunk.map(chunk =>
          processChunk([chunk], optionsSets, accumulatedSuggestions, rowIndex),
        );
        // wait for all chunk requests to finish.
        await Promise.all(promises);

        transformValues(accumulatedSuggestions);

        updatedFormData = upsertValues(
          updatedFormData,
          Object.entries(accumulatedSuggestions).map(([path, value]) => ({
            path,
            value,
          })),
        );

        wittyForm?.onChange({
          formData: updatedFormData,
        } as unknown as IChangeEvent);
      }

      alertApi.post({
        message: `Autocomplete done!`,
        severity: 'success',
      });
    } catch (error: any) {
      handleWittyError(error, alertApi);
    } finally {
      setLoading(false);
    }
  }, [
    wittyTable,
    subscribedOptionsSetIds,
    rowChunks,
    alertApi,
    processChunk,
    wittyForm,
    transformValues,
  ]);

  // handle the user clicking the "Autocomplete" button
  const handleSuggestClick = useCallback(() => {
    // if we detect the user already has some data => open the override warning
    if (shouldOverride) {
      setOverrideWarningOpen(true);
    } else {
      // otherwise, just call suggest
      suggest();
    }
  }, [shouldOverride, suggest]);

  const handleOverrideConfirm = useCallback(() => {
    setOverrideWarningOpen(false);
    suggest();
  }, [suggest]);

  const handleOverrideCancel = useCallback(() => {
    setOverrideWarningOpen(false);
  }, []);

  return (
    <WbWidget
      title={props.uiSchema?.['ui:title'] || props.title}
      cardStyle={{ borderBottom: props.items.length > 0 ? 'none' : 'auto' }}
      actions={
        <>
          {wittyEnabled && (
            <WittyNoContextTooltip
              triggeringColumns={wittyTable?.triggeringColumns ?? []}
              hide={!buttonDisabled}
            >
              <span
              // disabled elements won't fire mouse events so we use a span
              >
                <AiGenerateActionButton
                  style={{ marginRight: theme.spacing(1) }}
                  loading={loading}
                  disabled={buttonDisabled}
                  onClick={handleSuggestClick}
                />
              </span>
            </WittyNoContextTooltip>
          )}
          {props.canAdd ? (
            <WbButton
              label="Add"
              disabled={!props.canAdd}
              icon={<AddIcon />}
              onClick={props.onAddClick}
              color="secondary"
            />
          ) : undefined}
        </>
      }
    >
      <ArrayTableContent {...props} disabled={loading || props.disabled} />
      <Dialog
        fullScreen={isMobile}
        open={overrideWarningOpen}
        onClose={handleOverrideCancel}
      >
        <DialogTitle style={{ backgroundColor: theme.palette.bkg.primary }}>
          Override Warning
        </DialogTitle>
        <DialogContent>
          <strong>Override values?</strong>
          <br />
          Generating AI autocomplete will override already entered values in
          Witty-aware fields.
          <br />
          <strong>This action can't be undone.</strong>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            onClick={handleOverrideCancel}
            variant="outlined"
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleOverrideConfirm}
            variant="contained"
            color="secondary"
          >
            Override
          </Button>
        </DialogActions>
      </Dialog>
    </WbWidget>
  );
};

export const WithContextWittyTableTemplate = (
  props: WittyTableTemplateProps,
) => {
  const alertApi = useApi(alertApiRef);

  const tableConfig = useMemo(() => {
    const parsedConfig = WittyTableTemplateConfigurationZod.safeParse(
      props.schema.witty ?? {},
    );

    if (parsedConfig.success === false) {
      alertApi.post({
        message: `Witty Table configuration error: ${parsedConfig.error.message}`,
        severity: 'error',
      });
      return null;
    }

    return parsedConfig.data;
  }, [props.schema, alertApi]);

  const additionalContext: string[] = useMemo(() => {
    if (!tableConfig) {
      return [];
    }

    return tableConfig.context ?? [];
  }, [tableConfig]);

  const triggeringColumns: { columnName: string; columnKey: string }[] =
    useMemo(() => {
      const schemaItems = props.schema.items as Record<string, any>; // injured typescript devs after reading this line: 13
      // key is column key, value is column title
      const allColumnsMap: Record<
        string,
        { columnName: string; columnKey: string }
      > = Object.entries(schemaItems.properties).reduce((acc, [key, value]) => {
        acc[key] = {
          columnName: ((value as Record<string, any>).title as string) ?? key,
          columnKey: key,
        };
        return acc;
      }, {} as Record<string, { columnName: string; columnKey: string }>);

      const allColumns = Object.values(allColumnsMap);
      if (allColumns.length === 0) {
        alertApi.post({
          message: `Witty Table configuration error: No columns found in the schema.`,
          severity: 'error',
        });
        return [];
      }
      const defaultColumn = allColumns[0];

      if (!tableConfig) {
        // by default let's take the first column
        return [defaultColumn];
      }

      const validatedTriggeringColumns =
        tableConfig.triggeringColumns?.flatMap(col => {
          const column = allColumnsMap[col];
          if (!column) {
            return [];
          }
          return [column];
        }) ?? [];

      if (validatedTriggeringColumns.length === 0) {
        return [defaultColumn];
      }
      return validatedTriggeringColumns;
    }, [alertApi, props.schema.items, tableConfig]);

  /**
   * The maximum number of rows to process in a single chunk.
   * This is a configurable parameter, with a default value of 20.
   * It can be set in the schema under the `witty.maxRowsPerChunk` key.
   */
  const maxRowsPerChunk = useMemo(() => {
    if (!tableConfig) {
      return DEFAULT_MAX_ROWS_PER_CHUNK;
    }

    return tableConfig.maxRowsPerChunk ?? DEFAULT_MAX_ROWS_PER_CHUNK;
  }, [tableConfig]);

  return (
    <WittyTableContextProvider
      additionalContext={additionalContext}
      tablePath={props.idSchema.$id}
      rowsNumber={props.items.length}
      triggeringColumns={triggeringColumns}
      maxRowsPerChunk={maxRowsPerChunk}
    >
      <WittyTableTemplate {...props} />
    </WittyTableContextProvider>
  );
};

export const WittyTableTemplateLayout = scaffolderPlugin.provide(
  createScaffolderLayout({
    name: 'WittyTableTemplate',
    component: WithContextWittyTableTemplate as any,
  }),
);
